import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from "react"
import { BackHandler, DeviceEventEmitter, Platform } from "~/ui/primitives"
import styled from "~/ui/styled"
import Animated, {
  useSharedValue,
  withTiming,
  useAnimatedStyle,
  runOnJS,
} from "~/ui/reanimated"
import { GestureDetector, Gesture } from "~/ui/gesture-handler"
import type { OpenPopupOptions, OpenPopupPromise, StackItem } from "./types"
import { popupBridge } from "./popup-bridge"
import { POPUP_CLOSE_REQUEST, OPEN_IMAGE_GALLERY } from "./popup-events"
import { openImageGalleryWithApi } from "~utils/open-image-gallery"
import type { ImageGalleryProps } from "~pages/app-stack/image-gallery/types"
import { ColumnFlex } from "~components/El"

// const SCREEN_HEIGHT = Dimensions.get("window").height

const Root = styled(ColumnFlex)<{ justify: string; $stretch?: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: ${500}px;
  z-index: 10001;
  pointer-events: auto;
  display: flex;
  justify-content: ${(p) => p.justify};
  align-items: ${(p) => (p.$stretch ? "stretch" : "center")};
`

/** 遮罩用 div，避免空 button 在 flex 布局下挡住弹窗内容 */
const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  border: none;
  padding: 0;
  margin: 0;
  background-color: rgba(0, 0, 0, 0.45);
  cursor: default;
`

const panelLayerStyle = {
  position: "relative" as const,
  zIndex: 1,
  pointerEvents: "auto" as const,
}

const AnimatedPanelCenter = styled(Animated.View)<{ $transparent?: boolean }>`
  border-radius: ${(p) => (p.$transparent ? 0 : 16)}px;
  background-color: ${(p) => (p.$transparent ? "transparent" : "#fff")};
  overflow: ${(p) => (p.$transparent ? "visible" : "hidden")};
  position: relative;
  z-index: 1;
  pointer-events: auto;
`

const FullscreenPanel = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  align-self: stretch;
  min-height: 0;
  overflow: hidden;
`

const AnimatedPanelBottom = styled(Animated.View)`
  background-color: #fff;
  border-radius: 18px 18px 0 0;
  width: 100%;
  max-width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  z-index: 1;
  pointer-events: auto;
`

type PopupApi = {
  open: <T = any>(options: OpenPopupOptions) => OpenPopupPromise<T>
  close: (...args: any[]) => void
  closeAll: (result?: any) => void
}

const PopupContext = createContext<PopupApi | null>(null)

export const usePopupContext = () => useContext(PopupContext)

/**
 * 每个弹窗单独成组件，内部使用 useSharedValue（合法）
 * 负责 entrance / exit 动画、手势、以及在动画结束后通知父组件 removal
 */
const PopupItem: React.FC<{
  item: StackItem
  isTop: boolean
  isClosing: boolean
  closeResult?: any
  closePopup: (id: number) => void
  onRequestClose: (id: number, result?: any) => void
}> = ({ item, isTop, isClosing, closeResult, closePopup, onRequestClose }) => {
  const { options } = item
  const popupId = item.id
  const type = options.type ?? "center"
  const isFullscreen = type === "fullscreen"

  // H5：初始即为可见态，不依赖 onLayout 触发动画（避免与 ref/onLayout 循环）
  const bottomTranslateY = useSharedValue(0)
  const bottomHeight = useSharedValue(0)
  const centerScale = useSharedValue(1)
  const centerOpacity = useSharedValue(1)
  const backdropOpacity = useSharedValue(1)

  const rawContent = typeof options.content === "function" ? options.content() : options.content
  const handleClose = useCallback(() => closePopup(popupId), [closePopup, popupId])
  const Content = React.isValidElement(rawContent)
    ? React.cloneElement(rawContent, {
        popupId,
        onSuccess: () => {
          const props = rawContent.props as { onSuccess?: () => void }
          props.onSuccess?.()
          handleClose()
        },
        onClose: () => {
          const props = rawContent.props as { onClose?: () => void }
          props.onClose?.()
          handleClose()
        },
      } as Record<string, unknown>)
    : rawContent

  useEffect(() => {
    if (isClosing) exit(closeResult)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosing])

  const notifyClose = useCallback(
    (result?: any) => {
      onRequestClose(popupId, result)
    },
    [popupId, onRequestClose]
  )


  // exit helper (will run animation then call onRequestClose)
  const exit = (result?: any) => {
    if (Platform.OS === "web") {
      notifyClose(result)
      return
    }
    if (type === "bottom") {
      bottomTranslateY.value = withTiming(
        bottomHeight.value,
        { duration: 200 },
        () => runOnJS(notifyClose)(result)
      )
    } else {
      centerScale.value = withTiming(0, { duration: 200 })
      centerOpacity.value = withTiming(0, { duration: 200 }, () =>
        runOnJS(notifyClose)(result)
      )
    }
    backdropOpacity.value = withTiming(0, { duration: 200 })
  }

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value
  }))

  const bottomStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: bottomTranslateY.value }],
  }))

  const centerStyle = useAnimatedStyle(() => ({
    opacity: centerOpacity.value,
    transform: [{ scale: centerScale.value }],
  }))

  const bottomGesture = Gesture.Pan()
    // 仅在明显向下拖动时才接管手势用于关闭，向上滑动让位给内部 ScrollView（修复安卓内容无法滚动）
    .activeOffsetY(12)
    .failOffsetY(-12)
    .onUpdate((e) => {
      const transY = e.translationY
      if (transY > 0) {
        bottomTranslateY.value = transY
      }
    })
    .onEnd((e) => {
      const transY = e.translationY
      if (transY > 120) {
        runOnJS(exit)()
      } else {
        bottomTranslateY.value = withTiming(0, { duration: 200 })
      }
    })

  const disableBackdropClose = !!options.disableBackdropClose

  if (isFullscreen) {
    return (
      <Root justify="flex-start" $stretch>
        <Backdrop
          style={backdropStyle}
          role="presentation"
          aria-hidden
          onClick={() => {
            if (!disableBackdropClose && isTop) exit(null)
          }}
        />
        <FullscreenPanel>{Content}</FullscreenPanel>
      </Root>
    )
  }

  return (
    <Root justify={type === "bottom" ? "flex-end" : "center"} $stretch={type === "bottom"}>
      <Backdrop
        style={backdropStyle}
        role="presentation"
        aria-hidden
        onClick={() => {
          if (!disableBackdropClose && isTop) exit(null)
        }}
      />

      {type === "bottom" && (
        <GestureDetector gesture={bottomGesture}>
          <AnimatedPanelBottom
            onLayout={(e) => {
              bottomHeight.value = e.nativeEvent.layout.height
            }}
            style={[
              bottomStyle,
              panelLayerStyle,
              {
                width: options.width ?? "100%",
                height: options.height || "auto",
              },
            ]}
          >
              {Content}
          </AnimatedPanelBottom>
        </GestureDetector>
      )}

      {type === "center" && (
        <AnimatedPanelCenter
          $transparent={options.transparent}
          style={[
            centerStyle,
            panelLayerStyle,
            { width: options.width ?? "87.7863%", height: options.height ?? "auto" },
          ]}
        >
          {Content}
        </AnimatedPanelCenter>
      )}
      
      
    </Root>
  )
}

const PopupItemMemo = React.memo(PopupItem, (prev, next) => {
  return (
    prev.item.id === next.item.id &&
    prev.isClosing === next.isClosing &&
    prev.isTop === next.isTop &&
    prev.closePopup === next.closePopup
  )
})

export const PopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<StackItem[]>([])
  const idRef = useRef(0)
  const closingResultRef = useRef<Map<number, any>>(new Map())
  const closeImplRef = useRef<(arg1?: any, arg2?: any) => void>(() => {})

  /**
   * 打开弹窗
   * @param options 
   * @returns promise
   */
  const open = <T = any,>(options: OpenPopupOptions): OpenPopupPromise<T> => {
    const id = idRef.current++
    let resolver: ((v?: any) => void) | null = null
    const promise = new Promise<T>((resolve) => {
      resolver = resolve
    })
    const item: StackItem = { id, options, resolver }
    setStack((prev) => [...prev, item])
    return Object.assign(promise, { id })
  }

  // 在退出动画完成后，PopupItem 调用 onRequestClose 函数来关闭弹出窗口
  const onRequestClose = (id: number, result?: any) => {
    setStack((prev) => {
      const target = prev.find((p) => p.id === id)
      if (target?.resolver) {
        try {
          target.resolver(result)
        } catch (e) {
          // ignore
        }
      }
      closingResultRef.current.delete(id)
      return prev.filter((p) => p.id !== id)
    })
  }

  const closingIdsRef = useRef<Set<number>>(new Set())

  // 关闭：在 closingIds 中标记 id，PopupItem 通过 options 属性看到此 id。
  const close = (arg1?: any, arg2?: any) => {
    // 推荐新签名：close(id?: number, result?: any)
    // 兼容旧签名：close(result?: any, id?: number)
    const id = typeof arg1 === "number" ? (arg1 as number) : (typeof arg2 === "number" ? (arg2 as number) : undefined)
    const result = typeof arg1 === "number" ? arg2 : arg1

    const removeFromStack = (prev: StackItem[]) => {
      let target: StackItem | undefined

      if (id !== undefined) {
        target = prev.find((p) => p.id === id)
      } else {
        target = prev[prev.length - 1]
      }

      if (!target) return prev

      if (target.resolver) {
        try {
          target.resolver(result)
        } catch (e) {
          // ignore
        }
      }
      closingResultRef.current.delete(target.id)
      closingIdsRef.current.delete(target.id)
      return prev.filter((p) => p.id !== target.id)
    }

    // H5：异步回调（如 WebSocket onData）里 flushSync 可能无效，直接 setState
    if (Platform.OS === "web") {
      setStack(removeFromStack)
      return
    }

    setStack((prev) => {
      let target: StackItem | undefined

      if (id !== undefined) {
        target = prev.find((p) => p.id === id)
      } else {
        target = prev[prev.length - 1] // 默认关闭顶部
      }

      if (!target) return prev

      // 避免重复关闭
      if (closingIdsRef.current.has(target.id)) return prev
      closingIdsRef.current.add(target.id)
      closingResultRef.current.set(target.id, result)

      // 触发重新 render，让对应 PopupItem 进入 exit 动画（isClosing=true）
      return [...prev]
    })
  }

  const closeAll = (result?: any) => {
    if (Platform.OS === "web") {
      setStack((prev) => {
        if (!prev.length) return prev
        prev.forEach((item) => {
          if (item.resolver) {
            try {
              item.resolver(result)
            } catch (e) {
              // ignore
            }
          }
          closingResultRef.current.delete(item.id)
          closingIdsRef.current.delete(item.id)
        })
        return []
      })
      return
    }

    setStack((prev) => {
      if (!prev.length) return prev

      prev.forEach(item => {
        if (!closingIdsRef.current.has(item.id)) {
          closingIdsRef.current.add(item.id)
          closingResultRef.current.set(item.id, result)

        }
      })

      return [...prev] // 触发重新 render
    })
  }

  // Android 返回按钮：如果存在则关闭顶部
  useEffect(() => {
    const handler = () => {
      if (stack.length > 0) {
        close(null)
        return true
      }
      return false
    }
    BackHandler.addEventListener("hardwareBackPress", handler)
    return () => BackHandler.removeEventListener("hardwareBackPress", handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stack.length])

  const api = { open, close, closeAll }
  const apiRef = useRef(api)
  apiRef.current = api
  closeImplRef.current = close
  popupBridge.register(api)
  useEffect(() => () => popupBridge.unregister(api), [])

  const closePopupById = useCallback((id: number) => {
    closeImplRef.current(id)
  }, [])

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(POPUP_CLOSE_REQUEST, (id?: number) => {
      if (typeof id === "number") {
        closeImplRef.current(id)
      } else {
        closeImplRef.current()
      }
    })
    return () => sub.remove()
  }, [])

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(OPEN_IMAGE_GALLERY, (params: ImageGalleryProps) => {
      openImageGalleryWithApi(params, apiRef.current)
    })
    return () => sub.remove()
  }, [])

  return (
    <PopupContext.Provider value={api}>
      {children}

      {stack.map((item, idx) => {
        const isTop = idx === stack.length - 1
        const isClosing = closingIdsRef.current.has(item.id)
        const closeResult = closingResultRef.current.get(item.id)
        return (
          <PopupItemMemo
            key={item.id}
            item={item}
            isTop={isTop}
            isClosing={isClosing}
            closeResult={closeResult}
            closePopup={closePopupById}
            onRequestClose={(id, result) => {
              closingIdsRef.current.delete(id)
              onRequestClose(id, result)
            }}
          />
        )
      })}
    </PopupContext.Provider>
  )
}
